import { useParams } from '@tanstack/react-router';
import { useSnapshot } from 'valtio';
import { Dropdown } from '~/components2/Dropdown';
import Button from '~/components2/forms/Button';
import Icon from '~/components2/Icon';
import { useCustomDataRememberVisibility } from '~/hooks/useCustomDataRememberVisibility';
import layerStore from '~/store2/layers';
import {
  handleAllSectionsExpandToggle,
  handleTurnOffAllLayers,
  handleTurnOnAllLayers,
  handleUrbanRuralToggle,
  URBAN_BOUNDS,
} from './handlers';

interface GlobalCommandsProps {
  /** Leaves the picker is currently hiding; "Turn On All Layers" must not turn these on. */
  pickerHiddenLeafIds?: ReadonlySet<string>;
}

export default function GlobalCommands({ pickerHiddenLeafIds }: GlobalCommandsProps) {
  const { visibleLayerIds, projectLayer } = useSnapshot(layerStore);
  const { projectId } = useParams({ strict: false }) as { projectId?: string };
  // Both all-layer gestures are durable, so they record the project's custom-data selection too.
  const persistProjectId = useCustomDataRememberVisibility() ? projectId : undefined;

  return (
    <Dropdown
      side="right"
      align="start"
      triggerElement={
        <Button
          aria-label="Layer actions"
          data-testid="layers-action-menu-trigger"
          alternate
          icon="lightning"
          size="icon"
        />
      }
      items={[
        {
          type: 'simple',
          'data-testid': 'global-commands-turn-on-all',
          label: 'Turn On All Layers',
          onClick: () => handleTurnOnAllLayers(pickerHiddenLeafIds, persistProjectId),
        },
        {
          type: 'simple',
          'data-testid': 'global-commands-turn-off-all',
          label: 'Turn Off All Layers',
          onClick: () => handleTurnOffAllLayers(persistProjectId),
        },
        { type: 'separator' },
        {
          type: 'simple',
          'data-testid': 'global-commands-expand-all-sections',
          label: 'Expand All Sections',
          onClick: () => handleAllSectionsExpandToggle(true),
        },
        {
          type: 'simple',
          'data-testid': 'global-commands-collapse-all-sections',
          label: 'Collapse All Sections',
          onClick: () => handleAllSectionsExpandToggle(false),
        },
        { type: 'separator' },
        {
          type: 'simple',
          'data-testid': 'global-commands-show-my-projects',
          label: (() => {
            const text = 'Show My Projects';

            if (projectLayer) {
              return (
                <div className="flex w-full items-center justify-between">
                  {text}
                  <Icon icon="check" />
                </div>
              );
            } else {
              return text;
            }
          })(),
          onClick: () => {
            layerStore.projectLayer = !projectLayer;
          },
        },
        {
          type: 'simple',
          'data-testid': 'global-commands-urban-rural',
          label: (
            <div className="w-full">
              {visibleLayerIds.has(URBAN_BOUNDS) ? (
                <div className="flex w-full items-center justify-between gap-2">
                  Urban/Rural Boundaries
                  <Icon icon="check" />
                </div>
              ) : (
                'Urban/Rural Boundaries'
              )}
            </div>
          ),
          onClick: handleUrbanRuralToggle,
        },
      ]}
    />
  );
}
